import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, Spinner, TextAreaField, TextField, useToast } from '@rooming/ui';
import { isProblem, NETWORK_PROBLEM_SLUG, PERSON_SEX_TEXT } from '@rooming/web-core';
import { useState, type ReactNode } from 'react';

// Pieces every backoffice screen uses: problem text, the "run a command, toast, refresh" helper, the reason dialog
// (nothing sensitive without a reason — BACKOFFICE_DESIGN §1.3) and the loading/error frame of a query.

const BY_SLUG: Record<string, string> = {
  [NETWORK_PROBLEM_SLUG]: 'Sin conexión. Revisá tu internet y probá de nuevo.',
  'credenciales-invalidas': 'Correo o contraseña incorrectos.',
  'mfa-invalido': 'Código incorrecto.',
  'sesion-vencida': 'Tu sesión terminó. Volvé a ingresar.',
  'elegir-pais': 'Tu correo tiene una cuenta en Chile y otra en Paraguay. Elegí con cuál querés ingresar.',
  'accion-no-permitida': 'No tenés permiso para esta acción.',
  'plaza-ocupada': 'Esa plaza ya está ocupada.',
  'plaza-incompatible': 'Esa habitación no admite a ese pasajero.',
  'solicitud-no-vigente': 'Esa solicitud ya fue resuelta o retirada.',
  'seleccion-cerrada': 'La selección no está abierta: no se pueden mover pasajeros.',
  'grupo-sin-firmante': 'El grupo no tiene firmante: un administrador debe registrarlo en Integrantes.',
  'intercambios-no-disponibles': 'Los intercambios entre pasajeros están deshabilitados para este grupo.',
  'transicion-invalida': 'La acción no corresponde al estado actual.',
  'estadia-no-editable': 'La estadía ya no se puede modificar.',
  'importacion-con-errores': 'El archivo fue rechazado: corregilo y validalo de nuevo.',
  'cambio-de-contrasena-requerido': 'Antes de continuar tenés que elegir tu contraseña.',
  'firmante-requerido': 'Es el firmante del grupo: designá a otro antes de darlo de baja.',
  'distribucion-confirmada': 'La distribución está confirmada: solo se puede ubicar a quien no tiene plaza.',
  'permiso-incompatible': 'Ese permiso no corresponde al tipo de usuario (hotel o coordinador).',
  'estadia-superpuesta': 'El grupo ya tiene otra estadía en esas fechas.',
  'habitacion-fisica-en-uso': 'Ese número de habitación ya está asignado en esas fechas.',
};

export function messageOf(error: unknown): string {
  if (isProblem(error)) {
    const fields = error.errors.map((field) => field.detail).join(' ');
    return `${BY_SLUG[error.slug] ?? error.title}${fields === '' ? '' : ` ${fields}`}`;
  }
  return 'Ocurrió un error inesperado. Probá de nuevo.';
}

/**
 * Runs a command and reports it: success toast + invalidation of the given query keys, or an error toast with the
 * server's reason. Returns whether it succeeded, so a dialog can stay open on failure.
 */
export function useCommand(): {
  readonly busy: boolean;
  readonly run: (
    work: () => Promise<unknown>,
    done: string,
    invalidate?: readonly (readonly unknown[])[],
  ) => Promise<boolean>;
} {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function run(
    work: () => Promise<unknown>,
    done: string,
    invalidate: readonly (readonly unknown[])[] = [],
  ): Promise<boolean> {
    setBusy(true);
    try {
      await work();
      toast('success', done);
      return true;
    } catch (error) {
      toast('error', messageOf(error));
      return false;
    } finally {
      setBusy(false);
      // Also after a failure: a refused command usually means the screen was stale.
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
    }
  }
  return { busy, run };
}

/** Confirmation that requires a written reason (3–500 characters): it is stored in the audit trail. */
export function ReasonDialog({
  open,
  title,
  confirmLabel,
  danger = false,
  busy,
  disabled = false,
  children,
  reasonLabel = 'Motivo',
  reasonHint = 'Queda registrado en la auditoría.',
  onConfirm,
  onClose,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly confirmLabel: string;
  /** The label and hint of the written text (default: "Motivo"). */
  readonly reasonLabel?: string;
  readonly reasonHint?: string;
  readonly danger?: boolean;
  readonly busy: boolean;
  /** Something else in the dialog is still missing (e.g. a selection). */
  readonly disabled?: boolean;
  readonly children?: ReactNode;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 3;
  const close = (): void => {
    if (busy) return;
    setReason('');
    onClose();
  };
  return (
    <Dialog
      open={open}
      title={title}
      onClose={close}
      actions={
        <>
          <Button variant="secondary" disabled={busy} onClick={close}>
            Cancelar
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            disabled={!valid || disabled}
            onClick={() => {
              onConfirm(reason.trim());
              setReason('');
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {children}
        <TextAreaField
          label={reasonLabel}
          hint={reasonHint}
          maxLength={500}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </div>
    </Dialog>
  );
}

/** Loading and error frame shared by every query-backed screen. */
export function Loaded<T>({
  query,
  children,
}: {
  readonly query: { isPending: boolean; isError: boolean; error: unknown; data: T | undefined; refetch: () => unknown };
  readonly children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <Spinner />;
  if (query.isError || query.data === undefined)
    return (
      <Alert tone="error" title="No pudimos cargar los datos">
        <p>{messageOf(query.error)}</p>
        <Button variant="secondary" className="mt-3" onClick={() => void query.refetch()}>
          Reintentar
        </Button>
      </Alert>
    );
  return <>{children(query.data)}</>;
}

export const STAY_STATUS: Record<string, { text: string; tone: 'neutral' | 'info' | 'success' | 'warning' }> = {
  DRAFT: { text: 'En preparación', tone: 'neutral' },
  OPEN: { text: 'Selección abierta', tone: 'info' },
  CONFIRMED: { text: 'Confirmada', tone: 'success' },
  REOPENED: { text: 'Reabierta', tone: 'warning' },
  CLOSED: { text: 'Cerrada', tone: 'neutral' },
};

/** A person's sex in words: the one definition lives in web-core (shared with the passenger app). */
export const SEX_TEXT = PERSON_SEX_TEXT;

/**
 * Administrative password reset (prompt11 §14): a TENANT_ADMIN sets a provisional password for another account, with a
 * reason. Every session of that account ends; an account without second factor (passenger, signatory, hotel, guide)
 * must choose its own at the next login.
 */
export function PasswordResetDialog({
  target,
  mustChange,
  busy,
  onConfirm,
  onClose,
}: {
  /** Whose password: the name shown in the dialog, or undefined when closed. */
  readonly target: string | undefined;
  /** The account has no second factor: it will be asked for its own password at the next login. */
  readonly mustChange: boolean;
  readonly busy: boolean;
  readonly onConfirm: (password: string, reason: string) => void;
  readonly onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const close = (): void => {
    setPassword('');
    onClose();
  };
  return (
    <ReasonDialog
      open={target !== undefined}
      title="Restablecer contraseña"
      confirmLabel="Restablecer"
      busy={busy}
      disabled={password.length < 12}
      onConfirm={(reason) => {
        onConfirm(password, reason);
        setPassword('');
      }}
      onClose={close}
    >
      <p>
        Contraseña provisoria para {target}. Entregásela por un canal seguro.{' '}
        {mustChange
          ? 'Al ingresar con ella tendrá que elegir su propia contraseña.'
          : 'Su verificación en dos pasos no cambia: la contraseña sola no alcanza para ingresar.'}{' '}
        Se cierran todas sus sesiones.
      </p>
      <TextField
        label="Contraseña provisoria"
        type="password"
        autoComplete="new-password"
        hint="Al menos 12 caracteres."
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
    </ReasonDialog>
  );
}
